import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'

import config from '@/config'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandHeading } from '@/utils/brand-typography'
import { Checkbox, CountryInput, IbanInput, SelectInput, TextInput, ZipCodeInput } from '@/modules/base/components/inputs'
import VatNumberInput from '@/modules/customer/components/edit/forms/information/vat-number-input'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import InfoOutlineIcon from '@assets/images/icons/v2/info-outline.svg'
import LockOutlineIcon from '@assets/images/icons/v2/lock-outline.svg'
import SepaLogo from '@assets/images/paymentLogos/payments_methods_sepa.png'
import type { BillingFormData } from '../../hooks/usePurchase'
import { brandSelectStyles } from '../../_helpers/brandSelectStyles'
import BrandSelectOption from './brand-select-option'

const selectComponents = { Option: BrandSelectOption }

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'start',
    gap: 12,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    borderRadius: theme.borderRadiusExtraExtraLarge,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.brand.divider}`,
    color: theme.palette.brand.text.primary,
    boxShadow: theme.shadowCard,
    // the inputs share the white of the card - TextInput's light variant would grey them; disabled and
    // read-only fields (e-mail) keep their grey so they still read as not editable
    '& input:not(:disabled):not([readonly])': {
      background: theme.palette.background.paper,
    },
    // a disabled field (the e-mail) is greyed, so it reads as not editable
    '& input:disabled': {
      background: theme.backgroundColor,
    },
    // the same for the dropdowns (Unternehmenssitz, Anrede) ...
    '& .react-select__control:not(.react-select__control--is-disabled)': {
      background: theme.palette.background.paper,
    },
    // ... and the box of an unticked checkbox (SEPA-Mandat) - ticked it keeps its brand colour
    '& input[type="checkbox"]:not(:checked):not(:disabled) + div': {
      background: theme.palette.background.paper,
    },
    // all fields alike (design): 52 px high, radius 8, a 1 px border
    '& input': {
      height: 52,
      borderRadius: theme.borderRadiusLarge,
      borderColor: theme.palette.brand.inputBorder,
    },
    // ... but not the search field inside a dropdown, which would push the value down (Unternehmenssitz)
    '& .react-select__control input': {
      height: 'auto',
      borderRadius: 0,
    },
    '& .react-select__control': {
      height: 52,
      minHeight: 52,
    },
    // the check box of the design system: 18 px, radius 4, ticked in the brand colour
    '& input[type="checkbox"] + div': {
      width: 18,
      minWidth: 18,
      height: 18,
      borderRadius: 4,
      border: `1px solid ${theme.palette.brand.inputBorder}`,
    },
    '& input[type="checkbox"]:checked + div': {
      borderColor: theme.palette.brand.main,
      background: theme.palette.brand.main,
    },
  },
  // a field the purchase is missing (after a click on "Kauf abschließen")
  fieldError: {
    '& input, & input:focus, & .react-select__control': {
      borderColor: theme.palette.brand.error,
    },
  },
  checkboxError: {
    '& input[type="checkbox"]:not(:checked) + div': {
      borderColor: theme.palette.brand.error,
    },
  },
  // 8 px below its field: the card's gap of 16 px minus 8
  error: {
    margin: '-8px 0 0',
    color: theme.palette.brand.error,
    fontSize: theme.brandTypography.text.tiny.fontSize,
  },
  // a field of a row (Vorname, Nachname) is no item of the card, so its error has no gap to pull back into
  rowField: {
    '& $error': {
      marginTop: 8,
    },
  },
  title: {
    margin: 0,
    ...brandHeading(theme, 'h6'),
  },
  fromAccount: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: theme.borderRadius,
    background: theme.palette.brand.light,
    color: theme.palette.brand.main,
  },
  // mobile: paired fields are stacked, so their labels fit into one line
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    [theme.breakpoints.down(768)]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 16,
    },
  },
  // ZipCodeInput puts PLZ and Ort side by side (35 / 65 %) - stacked on mobile
  zipCodeRow: {
    [theme.breakpoints.down(768)]: {
      '& > div:first-child': {
        flexDirection: 'column',
        gap: 16,
      },
      '& > div:first-child > div': {
        width: '100%',
        padding: 0,
      },
    },
  },
  divider: {
    margin: '4px 0',
    border: 'none',
    borderTop: `1px solid ${theme.palette.brand.divider}`,
  },
  salutation: {
    maxWidth: '50%',
    [theme.breakpoints.down(768)]: {
      maxWidth: 'none',
    },
  },
  // the e-mail of the account cannot be changed here: text in the secondary colour and a lock at the end
  emailField: {
    position: 'relative',
    '& input:disabled': {
      paddingRight: 44,
      color: theme.palette.brand.text.secondary,
      // Safari dims the text of a disabled field on its own
      WebkitTextFillColor: theme.palette.brand.text.secondary,
      opacity: 1,
    },
  },
  // over the right end of the field (52 px high, see the card's input rule)
  emailLock: {
    position: 'absolute',
    right: 14,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    height: 52,
    pointerEvents: 'none',
    color: theme.palette.brand.text.secondary,
  },
  hint: {
    display: 'flex',
    gap: 6,
    color: theme.palette.brand.text.secondary,
    // the 16 px icon centred on the first line of the 24 px high text
    '& svg': {
      flexShrink: 0,
      marginTop: 4,
    },
  },
  paymentOption: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    border: `1px solid ${theme.palette.brand.main}`,
    borderRadius: theme.borderRadiusLarge,
    background: theme.palette.brand.light,
  },
  paymentOptionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontWeight: theme.brandTypography.weights.semibold,
    '& img': {
      height: 18,
      marginLeft: 'auto',
    },
  },
  // the only payment method - shown as the selected radio option
  radioDot: {
    width: 14,
    height: 14,
    borderRadius: theme.borderRadiusPill,
    border: `4px solid ${theme.palette.brand.main}`,
    background: theme.palette.background.paper,
  },
  sepaMandateLabel: {
    paddingLeft: 12,
    fontWeight: theme.brandTypography.weights.medium,
  },
  // the star of a required field in the brand colour, as in the login - set here, so it looks the same
  // behind the label of every kind of field
  required: {
    '&::after': {
      content: '" *"',
      color: theme.palette.brand.main,
    },
  },
  mandateText: {
    margin: 0,
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.tiny.fontSize,
  },
  // the label of every field - also of the read-only e-mail, which would turn pale otherwise
  customClassLabel: {
    fontSize: theme.brandTypography.text.regular.fontSize,
    fontWeight: theme.brandTypography.weights.medium,
    color: theme.palette.brand.text.primary,
  },
  // bank and BIC below the IBAN, wrapped instead of cut off
  bank: {
    margin: '-8px 0 0',
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.small.fontSize,
    overflowWrap: 'anywhere',
  },
}))

interface BillingStepProps {
  data: BillingFormData & { salutation?: string, vatNumber?: string }
  // free plans need no payment data
  hideBankDetails?: boolean
  // the address was taken over from the account - only then the hint says so
  isFromAccount?: boolean
  // "Kauf abschließen" was clicked with data missing: the missing fields are named
  showErrors?: boolean
  // the changed fields only - the checkout merges them into its billing data
  onChange: (changes: Partial<BillingStepProps['data']>) => void
  onIBANChange: (isValid: boolean) => void
}

interface BankData {
  bankname?: string
  bic?: string
}

const salutationOptions = () => {
  const { salutations } = lang.upgrade.wizard.billing.contact
  return [
    { value: '', name: salutations.none },
    { value: salutations.female, name: salutations.female },
    { value: salutations.male, name: salutations.male },
    { value: salutations.diverse, name: salutations.diverse },
  ]
}

const BillingStep = ({ data, hideBankDetails = false, isFromAccount = false, showErrors = false, onChange, onIBANChange }: BillingStepProps) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const selectStyles = useMemo(() => brandSelectStyles(theme), [theme])
  const texts = lang.upgrade.wizard.billing
  const [bank, setBank] = useState<BankData | null>(null)
  const requiredLabel = clsx(classes.customClassLabel, classes.required)

  // only the changed fields: the browser's autofill fills several fields before the step renders again,
  // a merge with `data` here would let each field overwrite the one filled just before
  const update = (changes: Partial<BillingStepProps['data']>) => onChange(changes)
  const handleInput = (value: string, name: string) => update({ [name]: typeof value === 'string' ? value.trim() : value })

  useEffect(() => {
    if (!data.countryIso) update({ countryIso: config.defaultCountryAsISO2 })
  }, [])

  const options = salutationOptions()
  const isForeignCompany = data.countryIso && data.countryIso !== config.defaultCountryAsISO2

  // after a failed purchase: the required fields still empty
  const isMissing = (field: keyof BillingStepProps['data']) => showErrors && !data[field]
  const missingHint = (field: keyof BillingStepProps['data'], text: string = texts.errors.required) => isMissing(field) && (
    <p className={classes.error} role='alert' data-qs-id={`checkout-billing-${String(field)}-error`}>{text}</p>
  )

  const textField = (name: 'companyName' | 'street' | 'firstName' | 'lastName' | 'bankAccountHolder', label: string, dataQsId: string) => (
    <div className={clsx(isMissing(name) && classes.fieldError)}>
      <TextInput
        name={name}
        label={label}
        value={data[name]}
        onChange={handleInput}
        hideErrorMessage
        required
        dataQsId={dataQsId}
        customClassLabel={requiredLabel}
      />
    </div>
  )

  return (
    <div className={classes.root} data-qs-id='checkout-billing-step'>
      <section className={classes.card}>
        <h2 className={classes.title}>{texts.address.title}</h2>
        {isFromAccount && (
          <span className={classes.fromAccount} data-qs-id='checkout-billing-from-account'>
            <CheckRoundedIcon />
            {texts.address.fromAccount}
          </span>
        )}
        {textField('companyName', texts.address.companyName, 'checkout-billing-company-name')}
        {missingHint('companyName')}
        {textField('street', texts.address.street, 'checkout-billing-street')}
        {missingHint('street')}
        <div className={clsx((isMissing('zipCode') || isMissing('city')) && classes.fieldError)}>
          <ZipCodeInput
            zipCode={data.zipCode}
            city={data.city}
            zipCodeLabel={texts.address.zipCode}
            cityLabel={texts.address.city}
            onChange={({ zipCode, city }) => update({ zipCode, city })}
            hideErrorMessage
            required
            dataQsId='checkout-billing-zip-code'
            customClassLabel={requiredLabel}
            customClass={classes.zipCodeRow}
          />
        </div>
        {(isMissing('zipCode') || isMissing('city')) && (
          <p className={classes.error} role='alert' data-qs-id='checkout-billing-zip-code-error'>{texts.errors.required}</p>
        )}
        <div className={clsx(isForeignCompany && classes.row)}>
          <div className={clsx(isMissing('countryIso') && classes.fieldError)}>
            <CountryInput
              label={texts.address.country}
              value={data.countryIso}
              onChange={(option) => update({ countryIso: option?.iso2 || '' })}
              maxMenuHeight={260}
              styles={selectStyles}
              customComponents={selectComponents}
              dataQsId='checkout-billing-country'
              customClassLabel={requiredLabel}
            />
          </div>
          {isForeignCompany && (
            <div className={clsx(isMissing('vatNumber') && classes.fieldError)}>
              <VatNumberInput
                vatNumber={data.vatNumber}
                useFormik={false}
                showValidationButton={false}
                onChange={handleInput}
                placeholder={lang.missingCompanyData.placeholder}
                dataQsId='checkout-billing-vat-number'
                customClassLabel={classes.customClassLabel}
              />
            </div>
          )}
        </div>
        {isForeignCompany && missingHint('vatNumber')}

        <hr className={classes.divider} />

        <h2 className={classes.title}>{texts.contact.title}</h2>
        <div className={classes.salutation}>
          <SelectInput
            name='salutation'
            label={texts.contact.salutation}
            allowCreate={false}
            notAsync
            options={options}
            value={options.find(option => option.value === (data.salutation || '')) || options[0]}
            handleChange={(option) => update({ salutation: option?.value || '' })}
            customSelectOptions={{ valueKey: 'value', labelKey: 'name', isClearable: false, isSearchable: false }}
            styles={selectStyles}
            customComponents={selectComponents}
            dataQsId='checkout-billing-salutation'
            customClassLabel={classes.customClassLabel}
          />
        </div>
        <div className={classes.row}>
          <div className={classes.rowField}>
            {textField('firstName', texts.contact.firstName, 'checkout-billing-first-name')}
            {missingHint('firstName')}
          </div>
          <div className={classes.rowField}>
            {textField('lastName', texts.contact.lastName, 'checkout-billing-last-name')}
            {missingHint('lastName')}
          </div>
        </div>
        {/* the invoice always goes to the e-mail address of the account, so it is shown read-only -
            the label keeps its normal colour, only the field reads as not editable */}
        <div className={classes.emailField}>
          <TextInput
            name='email'
            label={texts.contact.email}
            value={data.email}
            readOnly
            readOnlyLabelEnabled
            disabled
            dataQsId='checkout-billing-email'
            customClassLabel={requiredLabel}
          />
          <span className={classes.emailLock} data-qs-id='checkout-billing-email-lock'>
            <SVGIcon svg={LockOutlineIcon} width={16} height={16} fill='currentColor' />
          </span>
        </div>
        <div className={classes.hint}>
          <SVGIcon svg={InfoOutlineIcon} width={16} height={16} fill='currentColor' />
          {texts.contact.invoiceHint}
        </div>
      </section>

      {!hideBankDetails && (
        <section className={classes.card}>
          <h2 className={classes.title}>{texts.payment.title}</h2>
          <div className={classes.paymentOption} data-qs-id='checkout-billing-sepa'>
            <div className={classes.paymentOptionHeader}>
              <span className={classes.radioDot} aria-hidden />
              {texts.sepa.title}
              <img src={SepaLogo} alt={texts.sepa.title} />
            </div>
            {textField('bankAccountHolder', texts.payment.accountHolder, 'checkout-billing-account-holder')}
            {missingHint('bankAccountHolder')}
            <div className={clsx(isMissing('bankAccountIBAN') && classes.fieldError)}>
              <IbanInput
                name='bankAccountIBAN'
                label={texts.payment.iban}
                value={data.bankAccountIBAN}
                onInputChanged={handleInput}
                handleValidation={(isValid: boolean, bankData?: BankData) => {
                  onIBANChange(isValid)
                  setBank(isValid ? bankData || null : null)
                }}
                required
                dataQsId='checkout-billing-iban'
                customClassLabel={requiredLabel}
              />
            </div>
            {missingHint('bankAccountIBAN')}
            {/* after a valid IBAN: its bank and BIC, wrapped instead of cut off */}
            {bank?.bankname && <div className={classes.bank} data-qs-id='checkout-billing-bank'>{texts.payment.bank(bank.bankname, bank.bic)}</div>}
            <div className={clsx(isMissing('sepaMandate') && classes.checkboxError)}>
              <Checkbox
                name='sepaMandate'
                checked={Boolean(data.sepaMandate)}
                onChange={(value: boolean) => update({ sepaMandate: value })}
                dataQsId='checkout-billing-sepa-mandate'>
                <div className={clsx(classes.sepaMandateLabel, classes.required)}>{texts.payment.sepaMandate}</div>
              </Checkbox>
            </div>
            {missingHint('sepaMandate', texts.errors.sepaMandate)}
            <p className={classes.mandateText}>{texts.sepa.mandateText}</p>
          </div>
        </section>
      )}
    </div>
  )
}

export default BillingStep
